import React, { useEffect, useState, useMemo } from "react";
import MultiSelectCheckboxGroup from "../../../components/CheckBoxFilter";
import LineChart from "../../../components/chartTypes/lineChart";
import Table from "../../../components/Table";
import Table2 from "../../../components/Table2";
import { vintageReportRupees } from "../../../services/vintageReports";
import { listVintage, listBranches } from "../../../services/filterApis";
import { useTranslation } from "react-i18next";
import { baseURL } from "../../../services";
import Loader from "../../../components/Loader";

// Branch colors for comparative mode
const BRANCH_COLORS = [
  { 
    bgColor: '#E3F2FD',  // Light Blue background
    textColor: '#0D47A1', // Dark Blue text
    borderColor: '#90CAF9' // Blue border
  },
  { 
    bgColor: '#E8F5E9',  // Light Green background
    textColor: '#1B5E20', // Dark Green text
    borderColor: '#A5D6A7' // Green border
  },
  { 
    bgColor: '#FFF3E0',  // Light Orange background
    textColor: '#E65100', // Dark Orange text
    borderColor: '#FFCC80' // Orange border
  }
];

type BranchColor = {
  bgColor: string;
  textColor: string;
  borderColor: string;
};

const VintageOverallReportSecond = () => {
  const { t } = useTranslation();
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >(() => ({
    period: ["month"],
    displaymode: ["1"],
  }));
  const [pendingFilters, setPendingFilters] = useState<
    Record<string, string[]>
  >(() => ({
    period: ["month"],
    displaymode: ["1"],
  }));
  const [applyFilter, setApplyFilter] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [chartData, setChartData] = useState<any[]>([]);
  const [VintageList, setVintageList] = useState<boolean>(false);
  const [Last6VintageList, setLast6VintageList] = useState<any[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);
  const [isDownloadDisabled, setIsDownloadDisabled] = useState(false);
  const [comparativeLoading, setComparativeLoading] = useState(false);
  const [comparativeTables, setComparativeTables] = useState<any[][]>([]);
  const [branchList, setBranchList] = useState<
    { branch_id: string; branch_name: string }[]
  >([]);
  const [branchColors, setBranchColors] = useState<Record<string, BranchColor>>({});

  const [principalDPDEwi, setPrincipalDPDEwi] = useState<{
    headers: { Header: string; accessor: string }[];
    data: {
      quarter: string;
      start_date: string;
      end_date: string;
      next_month_start_date: string;
      last_month_end_date: string;
      disb_loan_count: string;
      monthly_data: { month_no: string; percentage: string }[];
    }[];
  }>({
    headers: [
      { Header: "Month", accessor: "month_no" },
      { Header: "Percentage", accessor: "percentage" },
    ],
    data: [],
  });

  const branchIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (Array.isArray(branchList)) {
      branchList.forEach((branch) => {
        const id = branch?.branch_id;
        const name = branch?.branch_name;
        if (id && name) {
          map[id] = name;
        }
      });
    }
    return map;
  }, [branchList]);

  const getBranchName = (branchId: string): string => {
    if (branchIdToNameMap[branchId]) return branchIdToNameMap[branchId];
    const branch = branchList.find((b) => b.branch_id === branchId);
    if (branch) return branch.branch_name || branchId;
    return branchId;
  };

  const handleFilterChange = (updatedValues: Record<string, string[]>) => {
    setPendingFilters(updatedValues);
  };

  //handle cancel click
  const handleCancelClick = () => {
    setSelectedFilters({
      vintage: Last6VintageList,
      displaymode: ["1"],
    });
    setPendingFilters({
      vintage: Last6VintageList,
      displaymode: ["1"],
    });
    setApplyFilter(0);
    setValidationError(null);
    setIsValid(true);
    setIsDownloadDisabled(false);
    setBranchColors({});
  };

  //handle apply click
  const handleApplyClick = () => {
    const displaymodeRaw = pendingFilters.displaymode?.[0] || "consolidate";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const selectedBranches = pendingFilters.branch || [];
    const isComparative = displaymode === "comparative" || displaymode === "2";

    if (isComparative) {
      if (selectedBranches.length < 2 || selectedBranches.length > 3) {
        setValidationError(
          "Please select minimum two and maximum three branches for comparative mode."
        );
        setIsValid(false);
        setIsDownloadDisabled(true);
        return;
      }
      
      // Assign colors to branches
      const colors: Record<string, BranchColor> = {};
      selectedBranches.forEach((branchId, index) => {
        colors[branchId] = BRANCH_COLORS[index % BRANCH_COLORS.length];
      });
      setBranchColors(colors);
    }

    setSelectedFilters(pendingFilters);
    setValidationError("");
    setIsValid(true);
    setIsDownloadDisabled(false);
    setApplyFilter((prev) => prev + 1);
  };

  //handle download button API
  const handleDownloadClick = () => {
    const link = `${baseURL}generate/vintage/overall_by_amount_csv?vintage=${
      selectedFilters.vintage ?? ""
    }&relationType=${selectedFilters.relationType ?? ""}&productType=${
      selectedFilters.productType ?? ""
    }&state_id=${selectedFilters.state ?? ""}&highRiskBranch=${
      selectedFilters.highRiskBranch ?? ""
    }&cluster_id=${selectedFilters.cluster ?? ""}&branch_id=${
      selectedFilters.branch ?? ""
    }`;
    window.location.href = link;
  };

  //API to get vintage report rupees data
  const getGenerateVintageReportOverall = async () => {
    const displaymodeRaw = selectedFilters.displaymode?.[0] || "1";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const isComparative = displaymode === "comparative" || displaymode === "2";

    setLoading(true);

    try {
      if (isComparative) {
        setComparativeLoading(true);
        const selectedBranches = selectedFilters.branch || [];

        const branchPromises = selectedBranches.map((branchId) =>
          vintageReportRupees({
            ...selectedFilters,
            branch: [branchId],
          })
        );

        const branchResponses = await Promise.all(branchPromises);
        const comparativeData = branchResponses.map((res) =>
          Array.isArray(res.data) ? res.data : []
        );

        setComparativeTables(comparativeData);

        // Fetch data for all branches to get complete period list
        const allBranchesResponse = await vintageReportRupees({
          ...selectedFilters,
          branch: [],
        });

        setChartData(allBranchesResponse.data);
        transformData(allBranchesResponse.data);
      } else {
        const response = await vintageReportRupees(selectedFilters);
        setChartData(response.data);
        transformData(response.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setComparativeLoading(false);
    }
  };

  const transformData = (apiData: any[]) => {
    if (apiData.length > 0) {
      // Collect all unique month numbers from all quarters
      const allMonths = Array.from(
        new Set(
          apiData.flatMap((item: any) =>
            item.monthly_data.map((data: any) => data.month_no)
          )
        )
      ).sort((a, b) => Number(a) - Number(b)); // Ensure months are sorted numerically

      // Generate column headers dynamically
      const headers = [
        { Header: "Quarter", accessor: "quarter" }, // First column
        ...allMonths.map((monthNo) => ({
          Header: `Month ${monthNo}`,
          accessor: `dpd${monthNo}`,
        })),
      ];

      // Transform API data into table row format
      const transformedData = apiData.map((item: any) => {
        let row: any = { quarter: item.quarter };

        // Fill row data dynamically based on available months
        allMonths.forEach((monthNo) => {
          const monthData = item.monthly_data.find(
            (data: any) => data.month_no === monthNo
          );
          row[`dpd${monthNo}`] = monthData ? monthData.percentage : "N/A"; // Default to "N/A" if data is missing
        });

        return row;
      });

      setPrincipalDPDEwi({ headers, data: transformedData });
    } else {
      setPrincipalDPDEwi({
        headers: [
          { Header: "Month", accessor: "month_no" },
          { Header: "Percentage", accessor: "percentage" },
        ],
        data: [],
      });
    }
  };

  const getVintageList = async () => {
    try {
      const response = await listVintage();
      const lastSixValues = response.data
        .slice(-6)
        .map((item: any) => item.value);
      setLast6VintageList(lastSixValues);
      setSelectedFilters((prev) => ({
        ...prev,
        vintage: lastSixValues,
      }));
      setPendingFilters((prev) => ({
        ...prev,
        vintage: lastSixValues,
      }));
      setVintageList(true);
    } catch (err) {
      console.error(err);
    }
  };

  const getRandomColor = () => {
    return `#${Math.floor(Math.random() * 16777215)
      .toString(16)
      .padStart(6, "0")}`;
  };

  const dataSets = chartData?.map((quarterData: any, index: number) => {
    return {
      label: quarterData.quarter,
      data: quarterData.monthly_data
        ? quarterData.monthly_data?.map((m: any) => {
            return parseFloat(m?.percentage) || 0;
          })
        : new Array(12).fill(0), // Ensure missing data is handled
      borderColor: getRandomColor(),
    };
  });

  useEffect(() => {
    if (VintageList && Last6VintageList.length > 0) {
      getGenerateVintageReportOverall();
    }
  }, [VintageList, applyFilter]);

  useEffect(() => {
    getVintageList();
  }, []);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await listBranches(selectedFilters.state || []);
        const branches = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
          ? response
          : [];
        setBranchList(branches);
      } catch (error) {
        console.error("Error loading branches", error);
        setBranchList([]);
      }
    };

    fetchBranches();
  }, [selectedFilters.state]);

  const filters = [
    { key: "state", type: "select", label: t("filters.state") },
    { key: "cluster", type: "select", label: t("filters.cluster") },
    {
      key: "highRiskBranch",
      type: "select",
      label: t("filters.highRiskBranch"),
    },
    {
      key: "displaymode",
      type: "singleSelect",
      label: t("filters.displaymode"),
    },
    { key: "branch", type: "select", label: t("filters.branch") },
    { key: "productType", type: "select", label: t("filters.productType") },
    { key: "relationType", type: "select", label: t("filters.relationType") },
    { key: "vintage", type: "select", label: t("filters.vintage") },
  ];

  // Format for comparative table
  const formatComparativeTable = () => {
    if (!comparativeTables.length || !selectedFilters.branch?.length) return null;

    // Create combined data structure
    const branchDataMap = new Map<string, any[]>();
    (selectedFilters.branch || []).forEach((branchId, index) => {
      branchDataMap.set(branchId, comparativeTables?.[index] || []);
    });

    // Get all unique months from all branches
    const allMonths = Array.from(
      new Set(
        Array.from(branchDataMap.values()).flatMap((branchData) =>
          branchData.flatMap(
            (quarter: any) =>
              quarter.monthly_data?.map((m: any) => m.month_no) || []
          )
        )
      )
    ).sort((a, b) => Number(a) - Number(b));

    // Create combined data using quarter match
    const combinedData = (chartData || []).map((quarterData) => {
      const row: any = { quarter: quarterData.quarter };

      // For each month and each branch, fill branch_{branchId}_month_{monthNo}
      allMonths.forEach((monthNo) => {
        (selectedFilters.branch || []).forEach((branchId) => {
          const branchData = branchDataMap.get(branchId) || [];
          const quarter = branchData.find(
            (q: any) => q.quarter === quarterData.quarter
          );
          const month = quarter?.monthly_data?.find(
            (m: any) => m.month_no === monthNo
          );
          row[`branch_${branchId}_month_${monthNo}`] = month
            ? month.percentage
            : "N/A";
        });
      });

      return row;
    });

    return (
      <Table2
        columns={[
          // Quarter column
          {
            Header: "Quarter",
            accessor: "quarter",
            sticky: "left",
            style: {
              fontWeight: "bold",
              borderRight: "1px solid #000",
              backgroundColor: "#f8f9fa", // Light gray background
            },
          },
          // Month columns with branches as sub-columns
          ...allMonths.map((monthNo) => ({
            Header: `Month ${monthNo}`,
            headerStyle: {
              borderBottom: "1px solid #000",
              borderRight: "1px solid #000",
              backgroundColor: "#ffffff", // White background for header
            },
            columns: (selectedFilters.branch || []).map((branchId) => ({
              Header: getBranchName(branchId),
              accessor: `branch_${branchId}_month_${monthNo}`,
              Cell: ({ row }: { row: any }) => {
                const branchData = branchDataMap.get(branchId) || [];
                const quarterData = branchData.find(
                  (q: any) => q.quarter === row.original.quarter
                );
                const monthData = quarterData?.monthly_data?.find(
                  (m: any) => m.month_no === monthNo
                );
                return monthData ? monthData.percentage : "N/A";
              },
              style: {
                backgroundColor: branchColors[branchId]?.bgColor || "#f5f5f5",
                color: branchColors[branchId]?.textColor || "inherit",
                borderRight: `1px solid ${branchColors[branchId]?.borderColor || "#ddd"}`,
              },
              headerStyle: {
                backgroundColor: "#ffffff", // White background for sub-header
                color: branchColors[branchId]?.textColor || "inherit",
              },
            })),
          })),
        ]}
        data={combinedData}
        tableHead=""
        type="1"
        loading={comparativeLoading || loading}
        isComparative={true}
        branchCount={selectedFilters.branch?.length || 0}
        branchColors={branchColors}
        branchIds={selectedFilters.branch || []}
      />
    );
  };

  return (
    <div>
      {/* Page Content */}
      <div className="page-content">
        {/* Dynamic Grey Header */}
        <div className="header">
          {t("vintageReport.vintageSecond.pageTitle")}
        </div>

        {/* Main Content */}
        <div className="content px-3">
          <MultiSelectCheckboxGroup
            filters={filters}
            selectedValues={pendingFilters}
            onChange={handleFilterChange}
            isLoading={[loading, comparativeLoading]}
            buttons={[
              { label: t("buttons.apply"), onClick: () => handleApplyClick() },
              { label: t("buttons.cancel"), onClick: handleCancelClick },
              {
                label: t("buttons.download"),
                onClick: () => handleDownloadClick(),
                disabled: isDownloadDisabled,
              },
            ]}
          />
          {/* Show validation error if exists */}
          {validationError && (
            <div
              style={{
                color: "#B00020",
                backgroundColor: "#FDECEA",
                padding: "12px 16px",
                border: "1px solid #F5C6CB",
                borderRadius: "6px",
                fontWeight: "500",
                marginTop: "10px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              }}
            >
              ⚠️ {validationError}
            </div>
          )}
        </div>

        {/* Chart Section - Always shown */}
        <div className="mt-1 p-2">
          {!validationError && (
            <LineChart
              title={t("vintageReport.vintageSecond.title")}
              labels={[
                "0",
                "1",
                "2",
                "3",
                "4",
                "5",
                "6",
                "7",
                "8",
                "9",
                "10",
                "11",
                "12",
              ]}
              dataSets={dataSets}
              xLabel=""
              yLabel=""
            />
          )}
        </div>

        {/* Table Section */}
        <div className="p-3">
          {!validationError && (
            <>
              {applyFilter &&
              selectedFilters.displaymode?.[0]?.trim().toLowerCase() === "2" ? (
                <div style={{ width: "100%", overflow: "auto" }}>
                  {formatComparativeTable()}
                </div>
              ) : (
                <div style={{ width: "100%", overflow: "scroll" }}>
                  <Table
                    columns={principalDPDEwi.headers}
                    data={principalDPDEwi.data}
                    tableHead=""
                    type="1"
                    loading={loading}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VintageOverallReportSecond;